import { getCurrentLanguage } from 'hzero-front/lib/utils/utils';
import { BeaconInstance } from '../types';
import { getMenuPathAndExtract } from '../utils/helpers';
import { EVENT_CODES } from './constrants';

interface BehaviourReportInitOptions {
  openBehaviourReport: boolean;
}

export interface BehaviourReportProps {
  behaviourCode: string;
  behaviourName: string;
}

const timeout = 1000 * 10;
const longtimeout = 1000 * 60;

export default class BehaviourReport {
  private readonly beacon: BeaconInstance;

  private readonly cache: Map<string, { start: number; menuPath: string }> = new Map();
  private readonly pathCache: Map<
    string,
    { start: number; stack: number[]; menuPath: string }
  > = new Map();

  private open = false;

  constructor(beacon: BeaconInstance) {
    this.beacon = beacon;
  }

  public init({ openBehaviourReport }: BehaviourReportInitOptions) {
    this.open = openBehaviourReport;
  }

  public newReport({ behaviourCode }: BehaviourReportProps) {
    if (!this.open) return;
    const menuPath = getMenuPathAndExtract()?.join(',');
    if (!menuPath) return;
    const curLang = getCurrentLanguage();
    if (curLang !== 'zh_CN') return;
    this.cache.set(behaviourCode, {
      start: window.Date.now(),
      menuPath,
    });
  }

  public uploadReport({ behaviourCode, behaviourName }: BehaviourReportProps) {
    if (!this.open) return;
    const cacheObj = this.cache.get(behaviourCode);
    if (!cacheObj?.start) return;
    this.cache.delete(behaviourCode);
    const endTime = window.Date.now();
    const isTimeout = (cacheObj?.start || 0) - endTime > timeout;
    if (isTimeout) return;
    const behaviourDuration = cacheObj?.start && endTime - cacheObj?.start;
    if (behaviourDuration < 0 || behaviourDuration > timeout) return;
    this.beacon.onUserAction(EVENT_CODES.BEHAVIOUR_REPORT_EVENT, {
      behaviourCode,
      behaviourName,
      behaviourStartTime: cacheObj?.start,
      behaviourEndTime: endTime,
      behaviourDuration,
      behaviourScope: 'behaviour_report',
      behaviourMenuPath: cacheObj.menuPath,
    });
  }

  public newPathReport({ behaviourCode }: BehaviourReportProps, totalStep: number) {
    if (!this.open) return;
    const menuPath = getMenuPathAndExtract()?.join(',');
    if (!menuPath) return;
    const curLang = getCurrentLanguage();
    if (curLang !== 'zh_CN') return;
    this.pathCache.set(behaviourCode, {
      start: window.Date.now(),
      stack: new Array(totalStep - 1).fill(0).map((_, i) => i + 2),
      menuPath,
    });
  }

  public uploadPathReport({ behaviourCode, behaviourName }: BehaviourReportProps, step: number) {
    if (!this.open) return;
    const record = this.pathCache.get(behaviourCode);
    if (!record) return;
    const startTime = record.start;
    if (record.stack.length > 1) {
      const head = record.stack[0];
      if (head === step) {
        record.stack.shift();
      }
      return;
    } else if (record.stack.length === 1 && record.stack[0] !== step) {
      throw new Error('【行为路径埋点上报】错误的结束步骤');
      return;
    }
    this.pathCache.delete(behaviourCode);
    const endTime = window.Date.now();
    const isTimeout = (startTime || 0) - endTime > longtimeout;
    if (isTimeout) return;
    this.beacon.onUserAction(EVENT_CODES.BEHAVIOUR_REPORT_EVENT, {
      behaviourCode,
      behaviourName,
      behaviourStartTime: startTime,
      behaviourEndTime: endTime,
      behaviourDuration: startTime && endTime - startTime,
      behaviourScope: 'behaviour_report_path',
      behaviourMenuPath: record.menuPath,
    });
  }
}
